import React, { useState, useEffect } from 'react';
import api, { UPLOADS_URL } from '../api';

function PublicIndividualsMap() {
  const [performers, setPerformers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/api/public/individuals')
      .then(res => {
        setPerformers(res.data);
        setFiltered(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    let result = performers;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(p =>
        p.display_name?.toLowerCase().includes(s) ||
        p.city?.toLowerCase().includes(s) ||
        p.state?.toLowerCase().includes(s)
      );
    }
    if (stateFilter) {
      result = result.filter(p => p.state === stateFilter);
    }
    setFiltered(result);
  }, [search, stateFilter, performers]);

  const states = [...new Set(performers.map(p => p.state))].filter(Boolean).sort();

  if (loading) return <div className="loading">Loading performers...</div>;

  if (selected) {
    return (
      <div className="map-page">
        <button
          onClick={() => setSelected(null)}
          style={{ marginBottom: '1rem', padding: '0.5rem 1rem', background: '#3d2a1a', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif' }}
        >
          ← Back to Performers
        </button>
        <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', borderRadius: '4px', padding: '2rem' }}>
          <h1 style={{ marginTop: 0 }}>{selected.display_name}</h1>
          <p style={{ color: 'var(--text-muted)' }}>📍 {selected.city}, {selected.state}</p>

          {selected.photos && selected.photos.length > 0 && (
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {selected.photos.filter(Boolean).map((photo, i) => (
                <img key={i} src={`${UPLOADS_URL}/${photo}`} alt={`${selected.display_name} photo ${i + 1}`}
                  style={{ maxWidth: '400px', maxHeight: '400px', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border)' }}
/>
              ))}
            </div>
          )}

          <h3>About</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{selected.bio}</p>

          {selected.affiliated_group && <p><strong>Affiliated Group:</strong> {selected.affiliated_group}</p>}
          {selected.event_types && <><h3>Events</h3><p>{selected.event_types}</p></>}

          <h3>Contact & Social</h3>
          {selected.publish_full_name && selected.full_name && <p><strong>Name:</strong> {selected.full_name}</p>}
          {selected.publish_email && selected.email && <p><strong>Email:</strong> {selected.email}</p>}
          {selected.website && <p><strong>Website:</strong> <a href={selected.website.startsWith('http') ? selected.website : 'https://' + selected.website} target="_blank" rel="noopener noreferrer">{selected.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a></p>}
          {selected.facebook && <p><strong>Facebook:</strong> <a href={selected.facebook.startsWith('http') ? selected.facebook : 'https://' + selected.facebook} target="_blank" rel="noopener noreferrer">{selected.facebook.replace(/^https?:\/\/(www\.)?facebook\.com\//, '@').replace(/\/$/, '')}</a></p>}
          {selected.instagram && <p><strong>Instagram:</strong> <a href={selected.instagram.startsWith('http') ? selected.instagram : 'https://instagram.com/' + selected.instagram.replace(/^@/, '')} target="_blank" rel="noopener noreferrer">{('@' + selected.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/^@/, '').replace(/\/$/, ''))}</a></p>}
          {selected.tiktok && <p><strong>TikTok:</strong> <a href={selected.tiktok.startsWith('http') ? selected.tiktok : 'https://tiktok.com/@' + selected.tiktok.replace(/^@/, '')} target="_blank" rel="noopener noreferrer">{('@' + selected.tiktok.replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/, '').replace(/^@/, '').replace(/\/$/, ''))}</a></p>}
        </div>
      </div>
    );
  }

  return (
    <div className="map-page">
      <h1>Krampus Performers</h1>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name, city, or state..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
          <option value="">All States/Provinces</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={() => { setSearch(''); setStateFilter(''); }}
          style={{ padding: '0.6rem 1rem', background: '#3d2a1a', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontSize: '0.85rem' }}
        >
          Clear
        </button>
      </div>

      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        Showing {filtered.length} of {performers.length} performers
      </p>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <p>No performers found matching your search.</p>
        </div>
      ) : (
        <div className="list-grid">
          {filtered.map(performer => (
            <div
              key={performer.id}
              className="card"
              onClick={() => setSelected(performer)}
              style={{ cursor: 'pointer' }}
            >
              {performer.photos && performer.photos[0] ? (
                <img
                  src={`${UPLOADS_URL}/${performer.photos[0]}`}
                  alt={performer.display_name}
                  style={{ width: '100%', height: '200px', objectFit: 'contain', background: 'var(--dark-3)' }}
                />
              ) : (
                <div style={{ height: '200px', background: 'var(--dark-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '3rem' }}>
                  ⛧
                </div>
              )}
              <div className="card-body">
                <h3 className="card-title">{performer.display_name}</h3>
                <p className="card-location">📍 {performer.city}, {performer.state}</p>
                {performer.affiliated_group && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                    Group: {performer.affiliated_group}
                  </p>
                )}
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.5rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                  {performer.bio}
                </p>
                <button style={{ marginTop: '0.75rem', padding: '0.4rem 0.8rem', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontSize: '0.8rem' }}>
                  View Details →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PublicIndividualsMap;
